// reactionActors — "who reacted with what", resolved from whichever of the two
// sources is available.
//
// There are two, on purpose and during rollout BOTH may be live:
//   • `room_message_reactions` (migration 0056) — one row per person, carries
//     the reaction time, and is the source the push trigger reads.
//   • `room_messages.reactions` jsonb `{emoji: [uid]}` — what every shipped
//     client writes today, and what a flag-off client still reads.
// The jsonb is a MIRROR maintained by a trigger, so the two agree; the table is
// preferred only because it has the timestamps. When the table is not there yet
// the jsonb is the whole truth, and the names come from the AppContext user
// list instead of a join.
//
// Kept out of the component so the merge rule is testable — getting it wrong
// shows up as a popover that says "Someone" for half the room.

export interface ReactionActorRow {
  userId: string;
  name: string;
  emoji: string;
  at?: number;
}

/** One emoji and everyone who picked it, ordered as the pills are. */
export interface ReactionGroup {
  emoji: string;
  count: number;
  /** Includes the viewer, flagged, so the sheet can offer "tap to remove". */
  people: Array<{ userId: string; name: string; mine: boolean; at?: number }>;
}

/**
 * Build the who-reacted groups.
 *
 * `rows` is what `fetchReactionActors` returned (empty before 0056). `jsonb` is
 * the message's own reactions map. Rows win per (emoji, user) because they
 * carry `at`; any uid present only in the jsonb is still listed, so a reaction
 * written by a client that has not started filling the table yet is never
 * invisible.
 */
export function groupReactionActors(
  rows: ReactionActorRow[],
  jsonb: Record<string, string[]> | undefined,
  nameOf: (userId: string) => string | undefined,
  viewerId: string | undefined,
): ReactionGroup[] {
  const byEmoji = new Map<string, Map<string, { userId: string; name: string; mine: boolean; at?: number }>>();
  const add = (emoji: string, userId: string, name: string | undefined, at?: number) => {
    let bucket = byEmoji.get(emoji);
    if (!bucket) { bucket = new Map(); byEmoji.set(emoji, bucket); }
    const existing = bucket.get(userId);
    bucket.set(userId, {
      userId,
      // A real name always beats the 'Someone' placeholder, whichever source
      // supplied it first.
      name: name || existing?.name || 'Someone',
      mine: !!viewerId && userId === viewerId,
      at: at ?? existing?.at,
    });
  };

  for (const [emoji, ids] of Object.entries(jsonb ?? {})) {
    for (const uid of ids ?? []) add(emoji, uid, nameOf(uid));
  }
  for (const r of rows) add(r.emoji, r.userId, r.name || nameOf(r.userId), r.at);

  return [...byEmoji.entries()]
    .map(([emoji, bucket]) => ({
      emoji,
      count: bucket.size,
      people: [...bucket.values()].sort((a, b) => {
        // Oldest reaction first, like WhatsApp; unknown times sort last.
        if (a.at != null && b.at != null && a.at !== b.at) return a.at - b.at;
        if ((a.at == null) !== (b.at == null)) return a.at == null ? 1 : -1;
        return a.name.localeCompare(b.name);
      }),
    }))
    .filter((g) => g.count > 0)
    .sort((a, b) => b.count - a.count || a.emoji.localeCompare(b.emoji));
}

/** "Bilal reacted 😂 to your message" — the wording the web alert and the
 *  mobile push both use, so a staff member sees one sentence, not two. */
export const reactionAlertText = (actorName: string, emoji: string): string =>
  `${actorName || 'Someone'} reacted ${emoji} to your message`;
